// Apply offline-submitted form values to the performance summary view
// (the read-only `.work-item` block) and close any open edit areas, so the
// UI reflects the queued changes without waiting for a server roundtrip.

export function applyOfflineUpdateToForm(form) {
  if (!form) return

  const masterData = getMasterData()
  const ruleId = form.querySelector('[name$="[attendance_rule_id]"]')?.value

  form.querySelectorAll(".work-item-container").forEach(container => {
    const destroyInput = container.querySelector('[name$="[_destroy]"]')
    const isDestroyed = destroyInput?.value === "1" || destroyInput?.value === "true"
    if (isDestroyed) {
      container.style.display = "none"
      return
    }

    updateSummary(container, ruleId, masterData)
    syncOriginalDataAttributes(container)
    closeEditArea(container)
    delete container.dataset.unsaved
  })
}

// Clear the per-item "未保存" badge for every container in a form. Used when
// an offline submission was previously queued (so on subsequent page loads the
// items shouldn't still look unsaved — they're queued for sync).
export function clearUnsavedBadges(form) {
  if (!form) return
  form.querySelectorAll(".work-item-container[data-unsaved]").forEach(container => {
    delete container.dataset.unsaved
  })
}

// Mirror form input values from sourceForm to targetForm (matching by [name])
// then apply the same offline update to targetForm. Used to keep the
// individual/group views in sync after an offline submit in the other mode.
export function syncOfflineUpdateAcrossForms(sourceForm, targetForm) {
  if (!sourceForm || !targetForm) return
  copyMatchingFields(sourceForm, targetForm)
  applyOfflineUpdateToForm(targetForm)
}

function copyMatchingFields(sourceForm, targetForm) {
  const SKIP_NAMES = new Set(["authenticity_token", "_method", "utf8"])

  sourceForm.querySelectorAll("input[name], select[name], textarea[name]").forEach(source => {
    if (SKIP_NAMES.has(source.name)) return
    const t = source.type
    if (t === "submit" || t === "button" || t === "file" || t === "image" || t === "reset") return

    const escapedName = source.name.replace(/(["\\])/g, "\\$1")
    const target = targetForm.querySelector(`[name="${escapedName}"]`)
    if (!target) return

    if (t === "checkbox" || t === "radio") {
      target.checked = source.checked
      return
    }

    if (target.tagName === "SELECT" && target.closest('[data-controller="choices"]')) {
      setChoicesValue(target, source.value)
    } else {
      target.value = source.value
    }
  })
}

function setChoicesValue(select, value) {
  const choicesEl = select.closest('[data-controller="choices"]')
  const choicesController = window.Stimulus?.getControllerForElementAndIdentifier?.(choicesEl, "choices")
  if (choicesController?.setValue) {
    choicesController.setValue(value)
  } else {
    select.value = value
  }
}

function getMasterData() {
  const el = document.getElementById("performance-master-data")
  if (!el) return null
  try {
    return JSON.parse(el.textContent)
  } catch (e) {
    return null
  }
}

function updateSummary(container, ruleId, masterData) {
  const projectPartSelect = container.querySelector('[name$="[project_part_id]"]')
  const projectPartName = projectPartSelect?.selectedOptions?.[0]?.text?.trim() || "-"

  const timeSliceName = container.querySelector(".time-slice-name")?.value
  const timeSlice = ruleId && timeSliceName
    ? masterData?.time_slices?.[`${ruleId}:${timeSliceName}`]
    : null
  const startTime = formatTime(readValue(container, "sliceStart")) || timeSlice?.start_time || "--:--"
  const endTime = formatTime(readValue(container, "sliceEnd")) || timeSlice?.end_time || "--:--"

  const workTimeSpan = readValue(container, "workTimeSpan") || "0.00"
  const workManDaysRaw = readValue(container, "workManDays")
  const workManDays = Number.parseFloat(workManDaysRaw)
  const workManDaysText = Number.isFinite(workManDays) ? workManDays.toFixed(2) : "0.00"
  const toolTimeSpan = readValue(container, "toolTimeSpan") || "0.00"

  const workMain = container.querySelector(".work-main")
  if (workMain) workMain.textContent = projectPartName

  const workSub = container.querySelector(".work-sub")
  if (workSub) {
    workSub.textContent =
      `区分:終日／${startTime} 〜 ${endTime} （${workTimeSpan}h／${workManDaysText}人／機械${toolTimeSpan}h）`
  }
}

function readValue(container, fieldName) {
  return container.querySelector(`[data-performances--performance-field-target="${fieldName}"]`)?.value
}

function formatTime(value) {
  if (!value) return null
  return value.slice(0, 5)
}

function closeEditArea(container) {
  const editArea = container.querySelector(".work-edit-area")
  if (editArea) editArea.style.display = "none"
}

function syncOriginalDataAttributes(container) {
  const map = {
    originalProjectPartId:   '[data-performances--performance-field-target="projectPart"]',
    originalTimeSliceId:     '[data-performances--performance-field-target="timeSliceSelect"]',
    originalSliceStart:      '[data-performances--performance-field-target="sliceStart"]',
    originalSliceEnd:        '[data-performances--performance-field-target="sliceEnd"]',
    originalWorkTimeSpan:    '[data-performances--performance-field-target="workTimeSpan"]',
    originalWorkManDays:     '[data-performances--performance-field-target="workManDays"]',
    originalWorkTypeId:      '[data-performances--performance-field-target="workType"]',
    originalToolId:          '[data-performances--performance-field-target="tool"]',
    originalToolTimeSpan:    '[data-performances--performance-field-target="toolTimeSpan"]',
    originalToolManDays:     '[data-performances--performance-field-target="toolManDays"]',
    originalToolLeaseNumber: 'input[name$="[tool_lease_number]"]',
  }

  Object.entries(map).forEach(([dataKey, selector]) => {
    const el = container.querySelector(selector)
    if (el) container.dataset[dataKey] = el.value
  })
}
